import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Pill, X, Wand2, Check, AlertTriangle, Loader2 } from 'lucide-react';

import { BRAND } from '../../brand';
import { claimStoreDoseUnits } from '../BaseURL';
import useDialogGuard from './dialogGuard';

// ============================================================================
//  DoseUnitFixDialog — fix a DRUG once, not a claim thirty-one times
// ============================================================================
//
//  NHIA validates every medicine line on its dose unit and refuses the ones
//  without it, so a claim carrying such a line is rejected. The tools for it were
//  a completeness filter, which lists the CLAIMS, and the medication table inside
//  one claim's editor. Thirty-one claims meant opening thirty-one claims and
//  typing the same value into each.
//
//  But the fault is not per claim. The same drug is missing its unit on every
//  claim that carries it, because the gap is in the drug's own record. So this
//  lists MEDICATIONS — one row per drug, with how many claims and how many lines
//  it affects — takes a dose unit for each, and writes every matching line in the
//  month in one pass.
//
//  MOST ROWS ARRIVE ALREADY FILLED IN. The server derives a suggestion from the
//  drug's stored strength or, failing that, its own name — "LONGRIDE 30MG TABLET"
//  yields "30 mg" — and offers it only when it already satisfies the NHIA shape.
//  A malformed suggestion prefilled into the field would be a rejection typed for
//  you. So the officer is usually confirming, not typing.
//
//  DRY RUN FIRST, ALWAYS. The preview says how many lines and claims each value
//  will touch before anything is written, and only blank lines are ever touched —
//  a line already corrected by hand is never overwritten by a bulk pass launched
//  from a stale list.

//  The NHIA shape: a quantity, a space, then the unit. Mirrors DOSE_UNIT_RE in
//  claimStore.js, which enforces it on write — checked here too so the row can go
//  red as it is typed rather than failing on submit.
const DOSE_UNIT_RE = /^\d+(?:[.,]\d+)?\s+\S.*$/;

const th = {
  textAlign: 'left', padding: '8px 10px', fontSize: 10, fontWeight: 800,
  letterSpacing: '.06em', textTransform: 'uppercase', color: BRAND.inkFaint,
  background: '#F8FAFC', borderBottom: `1px solid ${BRAND.line}`,
  position: 'sticky', top: 0, zIndex: 1, whiteSpace: 'nowrap',
};
const td = {
  padding: '7px 10px', fontSize: 12.5, color: BRAND.ink,
  borderBottom: `1px solid ${BRAND.line}`, verticalAlign: 'middle',
};

export default function DoseUnitFixDialog({
  open, scope, onClose, onDone,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [meds, setMeds] = useState([]);
  const [values, setValues] = useState({});      // key -> typed dose unit
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  //  Called before the `!open` return below, because a hook may not be skipped.
  //  The typed dose units live only in `values` until Apply, so the backdrop must
  //  not be able to throw them away.
  const { panelRef, backdropProps } = useDialogGuard();

  //  Identity is the pair doseUnitGaps grouped by, so what is written back is
  //  matched by the same rule it was listed by.
  const keyOf = (m) => `${m.code || ''}::${m.name}`;

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setLoading(true); setError(null); setMeds([]); setValues({}); setResult(null);

    axios.get(claimStoreDoseUnits, { params: scope })
      .then(({ data }) => {
        if (!alive) return;
        if (!data?.success) { setError(data?.message || 'Could not read the medicine lines.'); return; }
        const list = data.medications || [];
        setMeds(list);
        //  Prefilled from the server's suggestion. Everything else starts blank
        //  and a blank row is SKIPPED on apply, never guessed at.
        setValues(Object.fromEntries(
          list.filter((m) => m.suggested).map((m) => [keyOf(m), m.suggested])));
        if (data.note) setError(data.note);
      })
      .catch((e) => { if (alive) setError(e?.response?.data?.message || e.message); })
      .finally(() => { if (alive) setLoading(false); });

    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, JSON.stringify(scope || {})]);

  const rows = useMemo(() => meds.map((m) => {
    const k = keyOf(m);
    const typed = String(values[k] ?? '').trim();
    return { ...m, k, typed, valid: !!typed && DOSE_UNIT_RE.test(typed) };
  }), [meds, values]);

  const ready = rows.filter((r) => r.valid);
  const badly = rows.filter((r) => r.typed && !r.valid);
  const blankRows = rows.filter((r) => !r.typed);
  const linesReady = ready.reduce((n, r) => n + r.lines, 0);

  const setOne = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));

  //  Fill every blank row that HAS a suggestion. Rows the server could not derive
  //  one for stay blank — this never invents a value, it only stops the officer
  //  retyping the ones already worked out.
  const acceptAllSuggested = () => setValues((prev) => {
    const next = { ...prev };
    for (const m of meds) {
      const k = keyOf(m);
      if (m.suggested && !String(next[k] ?? '').trim()) next[k] = m.suggested;
    }
    return next;
  });

  // ── Confirming, without a browser alert ───────────────────────────────────
  //  This used window.confirm(). That draws the operating system's own box,
  //  titled "localhost:3000 says", in a font and a layout nothing else in this
  //  application uses — and it cannot show a table, so the plan had to be flattened
  //  into a wall of plain text with manual line breaks.
  //
  //  It is also a modal ON TOP of a modal: two things to dismiss, and the list you
  //  are being asked about hidden behind the box asking about it.
  //
  //  So the confirmation happens IN THIS DIALOG. `plan` holds the dry run; while it
  //  is set the body shows what will be written and the footer offers Back or
  //  Write. Same surface, same styling, and the medications stay legible while the
  //  decision is made.
  const [plan, setPlan] = useState(null);

  //  TAKES THE LIST, rather than reading `ready` off state.
  //
  //  "Record all N" fills the inputs and previews in one gesture, and a setState
  //  is not visible to the same tick — so reading `ready` here would have
  //  previewed the values as they were BEFORE the fill, which on a first click is
  //  an empty plan and reads as the button doing nothing.
  const previewFor = async (list) => {
    if (!list.length) return;
    //  `stored_name` as well as `name`. The name SHOWN may have been resolved from
    //  this facility's catalogue for a line whose claim carried only a code, and the
    //  rows are matched on what is actually stored — sending only the display name
    //  would match nothing and report "0 lines" for every resolved drug.
    const items = list.map((r) => ({
      code: r.code, name: r.name, stored_name: r.stored_name, dose_unit: r.typed,
    }));

    setBusy(true); setError(null);
    try {
      const { data } = await axios.post(claimStoreDoseUnits,
        { ...scope, items, dryRun: true });

      const would = (data.results || []).filter((r) => r.status === 'would_fix');
      const lines = would.reduce((n, r) => n + (r.lines || 0), 0);

      if (!lines) {
        setError('Nothing to write — those lines already have a dose unit.');
        return;
      }
      setPlan({
        would,
        lines,
        //  Per-drug claim counts overlap, so they cannot be summed. The largest is
        //  the only honest single number, and it is labelled "up to".
        claims: would.reduce((n, r) => Math.max(n, r.claims || 0), 0),
        rejected: (data.results || []).filter((r) => r.status === 'invalid' || r.status === 'failed'),
      });
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  const preview = () => previewFor(ready);

  // ── THE ONES THE DRUG'S OWN NAME ALREADY ANSWERS ──────────────────────────
  //
  //  ASKED FOR: "most of the medication have the dose unit at the tail end —
  //  500mg. But national health wants 500, a space, then the unit. If there is a
  //  way we can fix it using that trick... so upon loading, the system reads and
  //  fixes those ones, before it shows the real medication without a dose unit,
  //  which we can still fix by entering the dose unit there."
  //
  //  So the readable ones are separated from the rest and offered as ONE action.
  //  They are not written on load without being asked: this is 26 medicines
  //  across hundreds of live claim lines, it cannot be undone, and the officer
  //  signing the batch is the one who should say yes. The gesture is a single
  //  click that goes straight to the same preview every other write goes through,
  //  which is as close to automatic as a permanent change to live claims should
  //  get - and it leaves exactly the "real" ones behind in the list.
  const readable = rows.filter((r) => r.suggested && DOSE_UNIT_RE.test(r.suggested));
  const unreadable = rows.filter((r) => !r.suggested);
  const readableLines = readable.reduce((n, r) => n + r.lines, 0);
  const recordAllReadable = () => {
    acceptAllSuggested();
    previewFor(readable.map((r) => ({ ...r, typed: r.suggested })));
  };

  const commit = async () => {
    if (!plan) return;
    //  Carried through from the dry run's own results, so the write targets exactly
    //  the rows the preview counted.
    const items = plan.would.map((r) => ({
      code: r.code, name: r.name, stored_name: r.stored_name, dose_unit: r.dose_unit,
    }));
    setBusy(true); setError(null);
    try {
      const { data } = await axios.post(claimStoreDoseUnits,
        { ...scope, items, dryRun: false });
      setResult(data);
      setPlan(null);
      if (onDone) onDone();
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div role="dialog" aria-modal="true" aria-label="Fix dose units"
      {...backdropProps}
      style={{
        position: 'fixed', inset: 0, zIndex: 90, background: 'rgba(15,23,42,.42)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 16px',
      }}>
      <div ref={panelRef} style={{
        width: '100%', maxWidth: 820, background: '#fff', borderRadius: 12,
        border: `1px solid ${BRAND.line}`, boxShadow: '0 24px 60px rgba(15,23,42,.28)',
        maxHeight: 'calc(100vh - 96px)', display: 'flex', flexDirection: 'column',
      }}>

        {/* ── header ── */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '13px 16px',
          borderBottom: `1px solid ${BRAND.line}`,
        }}>
          <Pill size={17} style={{ color: BRAND.greenDark, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: BRAND.ink }}>
              {result ? 'Dose units applied'
                : plan ? 'Confirm what will be written'
                  : 'Medications without a dose unit'}
            </div>
            <div style={{ fontSize: 11.5, color: BRAND.inkMuted }}>
              {result
                ? 'Written. The list behind this dialog has been refreshed.'
                : plan
                  ? `${plan.lines} medicine line${plan.lines === 1 ? '' : 's'} across up to `
                    + `${plan.claims} claim${plan.claims === 1 ? '' : 's'}`
                  : loading
                    ? 'Reading the medicine lines…'
                    : (error && !meds.length)
                      ? 'The list could not be read'
                      : `${meds.length} medication${meds.length === 1 ? '' : 's'} — fix each one once, `
                        + 'and every claim carrying it is fixed'}
            </div>
          </div>
          <button type="button" onClick={onClose} disabled={busy} aria-label="Close"
            style={{ border: 0, background: 'none', cursor: busy ? 'not-allowed' : 'pointer',
              padding: 4, color: BRAND.inkMuted, display: 'flex' }}>
            <X size={16} />
          </button>
        </div>

        {/* ── outcome ── */}
        {result ? (
          <div style={{ padding: 16, overflowY: 'auto', display: 'grid', gap: 10, background: '#F8FAFC' }}>
            <div style={{
              padding: '12px 14px', borderRadius: 9, fontSize: 13,
              background: '#E7F5EC', color: '#1B6631', border: '1px solid #BFE3CC',
            }}>
              <b style={{ fontSize: 15 }}>{result.linesWritten}</b> medicine line(s) now carry a
              dose unit, across {result.tally?.fixed || 0} medication(s).
            </div>
            {(result.results || []).some((r) => r.status === 'failed' || r.status === 'invalid') && (
              <div style={{ fontSize: 12.5, color: '#8A5510', lineHeight: 1.6 }}>
                {(result.results || [])
                  .filter((r) => r.status === 'failed' || r.status === 'invalid')
                  .map((r) => `${r.name}: ${r.reason}`).join(' · ')}
              </div>
            )}
          </div>
        ) : plan ? (
          /* ── the confirmation, IN this dialog rather than a browser alert ──
             The same table shape the editing step used, so the officer is
             checking the thing they just typed and not a reformatted summary of
             it. The counts are what the dry run actually found, per drug. */
          <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <div style={{
              padding: '11px 16px', fontSize: 12.5, lineHeight: 1.6,
              background: '#FDF2E3', color: '#8A5510',
              borderBottom: '1px solid #F2DCB8',
            }}>
              <b>This cannot be undone.</b> Only lines that are currently blank are
              touched — a dose unit somebody has already entered is left alone.
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Medication</th>
                  <th style={{ ...th, width: 150 }}>Dose unit</th>
                  <th style={{ ...th, width: 74, textAlign: 'right' }}>Claims</th>
                  <th style={{ ...th, width: 66, textAlign: 'right' }}>Lines</th>
                </tr>
              </thead>
              <tbody>
                {plan.would.map((r) => (
                  <tr key={`${r.code || ''}::${r.name}`}>
                    <td style={{ ...td, fontWeight: 700 }}>{r.name}</td>
                    <td style={td}>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 6,
                        background: '#E7F5EC', color: '#1B6631', fontWeight: 800,
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      }}>{r.dose_unit}</span>
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {(r.claims || 0).toLocaleString()}
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                      color: BRAND.inkMuted }}>
                      {(r.lines || 0).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/*  Anything the server refused, named rather than dropped. A value
                 that will not be written must not look like one that will. */}
            {plan.rejected.length > 0 && (
              <div style={{ padding: '10px 16px', fontSize: 12, lineHeight: 1.6,
                color: BRAND.danger, borderTop: `1px solid ${BRAND.line}` }}>
                Not written: {plan.rejected.map((r) => `${r.name} — ${r.reason}`).join(' · ')}
              </div>
            )}
          </div>
        ) : (
          <>
            {/* ── what this does ── */}
            <div style={{
              padding: '10px 16px', fontSize: 12, lineHeight: 1.6, color: BRAND.inkMuted,
              borderBottom: `1px solid ${BRAND.line}`, background: '#F8FAFC',
            }}>
              NHIA refuses a medicine line with no dose unit. A value typed here is
              written to <b>every line of that medication</b> in this month — lines that are
              currently blank only. The shape is a quantity, a space, then the
              unit: <b>500 mg</b>, <b>5 mL</b>.
            </div>

            {/* ── the ones their own name already answers ──────────────────
                 A ward name usually carries the strength: "AMOXICILLIN CAPS
                 500MG" is the right value in the wrong shape, and it was being
                 thrown away over a missing space. These are separated out and
                 offered as one click, so what is left in the list underneath is
                 the medicines that genuinely need someone to know the answer. */}
            {!loading && readable.length > 0 && (
              <div style={{
                margin: '10px 16px 0', padding: '10px 12px', borderRadius: 9,
                background: BRAND.greenSoft, border: `1px solid ${BRAND.greenDark}33`,
                display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
              }}>
                <Wand2 size={15} style={{ color: BRAND.greenDark, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 190, fontSize: 12, lineHeight: 1.55, color: BRAND.ink }}>
                  <b>{readable.length} of {rows.length}</b> say their dose unit in their own
                  name — {readableLines.toLocaleString()} line{readableLines === 1 ? '' : 's'} that can be
                  recorded without typing anything.
                  {unreadable.length > 0 && (
                    <>
                      {' '}The other <b>{unreadable.length}</b> need you to enter it below.
                    </>
                  )}
                  <div style={{ fontSize: 11, color: BRAND.inkMuted, marginTop: 2 }}>
                    {/*  Shown, not just described. "500MG becomes 500 mg" is the whole
                         change, and an officer should see it before agreeing to it. */}
                    e.g. <span style={{ fontWeight: 700 }}>{readable[0].name}</span>
                    {' → '}<span style={{ fontWeight: 800, color: BRAND.greenDark }}>{readable[0].suggested}</span>
                  </div>
                </div>
                <button type="button" onClick={recordAllReadable} disabled={busy}
                  title="Shows you exactly what would be written before anything changes"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '8px 13px', borderRadius: 8, fontSize: 12, fontWeight: 800,
                    font: 'inherit', border: '1px solid transparent', color: '#fff',
                    cursor: busy ? 'not-allowed' : 'pointer',
                    background: busy ? BRAND.inkFaint : BRAND.greenDark,
                  }}>
                  {busy ? <Loader2 size={13} className="spin" /> : <Wand2 size={13} />}
                  Record all {readable.length}
                </button>
              </div>
            )}

            {error && (
              <div style={{
                margin: '10px 16px 0', padding: '9px 11px', borderRadius: 8, fontSize: 12,
                background: '#FDF2E3', color: '#8A5510', border: '1px solid #F2DCB8',
              }}>{error}</div>
            )}

            {/* ── the rows ── */}
            <div style={{ flex: 1, minHeight: 160, overflow: 'auto' }}>
              {loading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: 8, padding: '40px 16px', fontSize: 12.5, color: BRAND.inkMuted }}>
                  <Loader2 size={16} className="spin" /> Reading the medicine lines…
                </div>
              ) : rows.length === 0 ? (
                <div style={{ padding: '36px 16px', textAlign: 'center', fontSize: 12.5, color: BRAND.inkFaint }}>
                  {/*  An EMPTY list and a FAILED read are not the same answer, and
                       this said the reassuring one for both — the screenshot showed
                       a SQL error and "every medicine line has a dose unit" one
                       above the other. An empty list is only good news when the
                       request actually succeeded. */}
                  {error
                    ? 'The list could not be read, so nothing is shown — see above.'
                    : 'Every medicine line in this month has a dose unit.'}
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={th}>Medication</th>
                      <th style={{ ...th, width: 74, textAlign: 'right' }}>Claims</th>
                      <th style={{ ...th, width: 66, textAlign: 'right' }}>Lines</th>
                      <th style={{ ...th, width: 190 }}>Dose unit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.k}>
                        <td style={td}>
                          <div style={{ fontWeight: 700, color: BRAND.ink }}>
                            {r.name}
                            {/*  Where that name came from. A facility whose claim file
                                 carries only the medicine code gets the name resolved
                                 from its own store_pro catalogue — useful, but an
                                 interpretation of a code rather than something the
                                 claim said, and the person typing a dose unit for it
                                 should know which they are looking at. */}
                            {r.name_from === 'catalogue' && (
                              <span style={{
                                marginLeft: 6, fontSize: 9.5, fontWeight: 800,
                                padding: '2px 5px', borderRadius: 4, verticalAlign: 'middle',
                                background: BRAND.greenSoft, color: BRAND.greenDark,
                              }}>
                                FROM CATALOGUE
                              </span>
                            )}
                            {r.name_from === 'code' && (
                              <span title="This code is not in the medication catalogue, so there is no name to show"
                                style={{
                                  marginLeft: 6, fontSize: 9.5, fontWeight: 800,
                                  padding: '2px 5px', borderRadius: 4, verticalAlign: 'middle',
                                  background: '#FFF8EC', color: '#8A5510',
                                  border: '1px solid rgba(224,139,47,.30)',
                                }}>
                                CODE ONLY
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 10.5, color: BRAND.inkFaint, marginTop: 1 }}>
                            {/*  Not repeated when the name IS the code — "CODE ONLY"
                                 above already says so, and printing it twice was the
                                 "HEPARIIN3 / HEPARIIN3" the screen used to show. */}
                            {r.name_from === 'code' ? 'not in the catalogue' : (r.code || 'no code')}
                            {r.dosage_form ? ` · ${r.dosage_form}` : ''}
                            {/*  A suggestion read off the NAME is an inference, not
                                 a recorded fact, and the two must not look alike to
                                 someone signing the claim off. */}
                            {r.suggested_from === 'name' && (
                              <span style={{ marginLeft: 6, color: '#8A5510', fontWeight: 700 }}>
                                suggested from the name
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {r.claims.toLocaleString()}
                        </td>
                        <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                          color: BRAND.inkMuted }}>
                          {r.lines.toLocaleString()}
                        </td>
                        <td style={td}>
                          <input
                            value={values[r.k] ?? ''}
                            onChange={(e) => setOne(r.k, e.target.value)}
                            placeholder={r.suggested || 'e.g. 500 mg'}
                            aria-label={`Dose unit for ${r.name}`}
                            aria-invalid={!!r.typed && !r.valid}
                            style={{
                              width: '100%', boxSizing: 'border-box', padding: '6px 8px',
                              fontSize: 12.5, borderRadius: 7, font: 'inherit',
                              color: BRAND.ink, outline: 'none', background: '#fff',
                              border: `1px solid ${r.typed
                                ? (r.valid ? '#2E9E4F' : BRAND.danger)
                                : BRAND.line}`,
                            }} />
                          {r.typed && !r.valid && (
                            <div style={{ fontSize: 10.5, color: BRAND.danger, marginTop: 2 }}>
                              Needs a quantity, a space, then the unit
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

          </>
        )}

        {/* ── actions ────────────────────────────────────────────────────────
            ONE footer for all three phases, outside the branches above. It was
            inside the editing branch, so the confirmation step had no buttons at
            all — which is the sort of thing that only shows up when the phase it
            belongs to is reached. */}
        <div style={{
          display: 'flex', gap: 8, padding: '12px 16px', alignItems: 'center',
          flexWrap: 'wrap', borderTop: `1px solid ${BRAND.line}`, background: '#fff',
        }}>
          {/* ── done ── */}
          {result ? (
            <>
              <div style={{ flex: 1, minWidth: 160, fontSize: 11.5, color: BRAND.inkMuted }}>
                Nothing else to do here.
              </div>
              <button type="button" onClick={onClose} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '9px 16px', borderRadius: 8, fontSize: 12.5, fontWeight: 800,
                font: 'inherit', border: '1px solid transparent', color: '#fff',
                cursor: 'pointer', background: BRAND.greenDark,
              }}>
                <Check size={13} /> Done
              </button>
            </>
          ) : plan ? (
            /* ── confirming ── */
            <>
              <div style={{ flex: 1, minWidth: 160, fontSize: 11.5, color: BRAND.inkMuted }}>
                {plan.would.length} medication{plan.would.length === 1 ? '' : 's'} ·{' '}
                {plan.lines} line{plan.lines === 1 ? '' : 's'}
              </div>
              {/*  BACK, not Cancel. Nothing has been written yet and the values are
                   still typed in, so the way out is to the list — not out of the
                   dialog, losing the work. */}
              <button type="button" onClick={() => setPlan(null)} disabled={busy} style={{
                padding: '9px 14px', borderRadius: 8, fontSize: 12.5, fontWeight: 700,
                font: 'inherit', cursor: busy ? 'not-allowed' : 'pointer',
                border: `1px solid ${BRAND.line}`, background: '#fff', color: BRAND.inkMuted,
              }}>Back</button>
              <button type="button" onClick={commit} disabled={busy}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '9px 16px', borderRadius: 8, fontSize: 12.5, fontWeight: 800,
                  font: 'inherit', border: '1px solid transparent', color: '#fff',
                  cursor: busy ? 'not-allowed' : 'pointer',
                  background: busy ? BRAND.inkFaint : BRAND.greenDark,
                }}>
                {busy ? <Loader2 size={13} /> : <Check size={13} />}
                {busy ? 'Writing…' : `Write ${plan.lines} line${plan.lines === 1 ? '' : 's'}`}
              </button>
            </>
          ) : (
            /* ── editing ── */
            <>
              {meds.some((m) => m.suggested) && (
                <button type="button" onClick={acceptAllSuggested} disabled={busy}
                  title="Fill every blank row that has a suggested value"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '8px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                    font: 'inherit', cursor: busy ? 'not-allowed' : 'pointer',
                    border: `1px solid ${BRAND.line}`, background: '#fff', color: BRAND.navy,
                  }}>
                  <Wand2 size={13} /> Use every suggestion
                </button>
              )}

              <div style={{ flex: 1, minWidth: 160, fontSize: 11.5, color: BRAND.inkMuted }}>
                {/*  With nothing listed there is nothing to say about values, and
                     "Every medication has a value" beside a failed read is the
                     same false all-clear the empty state was giving. */}
                {rows.length === 0
                  ? ''
                  : badly.length > 0
                    ? <span style={{ color: BRAND.danger, fontWeight: 700 }}>
                        {badly.length} value{badly.length === 1 ? '' : 's'} not in the NHIA shape
                      </span>
                    : blankRows.length > 0
                      ? `${blankRows.length} still blank — those are skipped`
                      : 'Every medication has a value'}
              </div>

              <button type="button" onClick={onClose} disabled={busy} style={{
                padding: '9px 14px', borderRadius: 8, fontSize: 12.5, fontWeight: 700,
                font: 'inherit', cursor: busy ? 'not-allowed' : 'pointer',
                border: `1px solid ${BRAND.line}`, background: '#fff', color: BRAND.inkMuted,
              }}>Cancel</button>

              <button type="button" onClick={preview} disabled={busy || !ready.length}
                title={ready.length
                  ? `Check what ${linesReady} line(s) would change`
                  : 'Type a dose unit for at least one medication'}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '9px 16px', borderRadius: 8, fontSize: 12.5, fontWeight: 800,
                  font: 'inherit', border: '1px solid transparent', color: '#fff',
                  cursor: busy || !ready.length ? 'not-allowed' : 'pointer',
                  background: busy || !ready.length ? BRAND.inkFaint : BRAND.greenDark,
                }}>
                {busy ? <Loader2 size={13} /> : <Check size={13} />}
                {/*  Says it opens a check rather than writing. "Apply" on a button
                     that then asks again is a button that lied about itself. */}
                {busy ? 'Checking…' : `Review ${ready.length || ''}`.trim()}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
